import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useProjectStore } from '@/store/projectStore'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import Modal from '@/components/common/Modal'
import ObjectPickerField from '@/pages/designers/page/ObjectPickerField'
import { loadTransactionObject } from '@/api/designers'
import {
  getIntegrationDesign,
  saveIntegrationDesign,
  syncToIOFlow,
  getIntegrationStatus,
  getOpenInIOFlowUrl,
  generateIntegrationDesign,
} from '@/api/integration'
import type {
  IntegrationDesign,
  IntegrationEvent,
  IntegrationEventArg,
  IntegrationArgType,
  IntegrationApi,
  IntegrationApiOperation,
  IntegrationStatus,
  ProjectFlow,
  SyncReport,
} from '@/api/integration'
import IntegrationFlowsSection from './IntegrationFlowsSection'

/**
 * Integration tab — two clearly separated directions:
 *
 *   EVENTS (Twasta ➜ IOFlow): hooks the application calls in IOFlow when
 *   something happens (record added / edited / deleted, action performed).
 *   Each event is routed through the object's follow-up actions and carries
 *   the record fields chosen as parameters; IOFlow flows subscribe to it and
 *   perform the follow-up work (send an email, notify a system, …).
 *
 *   APIS (IOFlow ➜ Twasta): operations IOFlow flows can call ON the
 *   application — add or update a record, read records, run a transaction
 *   action (approve / cancel / …). Published to IOFlow as callable services
 *   with their arguments derived from the object's fields.
 */

const TRIGGERS: { value: string; label: string }[] = [
  { value: 'add', label: 'Record added' },
  { value: 'edit', label: 'Record edited' },
  { value: 'delete', label: 'Record deleted' },
  { value: 'action', label: 'Action performed' },
]

const OPERATIONS: { value: IntegrationApiOperation; label: string; icon: string; hint: string }[] = [
  { value: 'add_record', label: 'Add a record', icon: 'add_circle',
    hint: 'IOFlow sends field values; Twasta creates the record (validations & business logic run).' },
  { value: 'update_record', label: 'Update a record', icon: 'edit_note',
    hint: 'IOFlow sends the record id (txn_id) + field values to change.' },
  { value: 'get_records', label: 'Read records', icon: 'manage_search',
    hint: 'IOFlow sends optional field filters; Twasta returns matching records.' },
  { value: 'run_action', label: 'Run an action', icon: 'play_circle',
    hint: 'IOFlow runs one of the transaction’s action buttons (e.g. approve / cancel) on a record.' },
  { value: 'bulk_upsert', label: 'Add or update many records', icon: 'dynamic_feed',
    hint: 'IOFlow sends a list of records in one call (a feed, a file, AI-classified rows). Each is matched on the key fields: an existing record is updated, a new one added — every record validated and saved on its own, with one result per record.' },
]

const LEGACY_CATEGORY_LABELS: Record<string, string> = {
  email: 'Send Email', whatsapp: 'Send WhatsApp', telegram: 'Send Telegram',
  http: 'HTTP / 3rd-party API', custom: 'Custom',
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
}

function isInboundApi(a: IntegrationApi): boolean {
  return Boolean((a.operation || '').trim())
}

function emptyEvent(): IntegrationEvent {
  return {
    event_name: '',
    display_name: '',
    description: '',
    source: { type: 'transaction', obj_name: '', trigger: 'add' },
    params: [],
    active: true,
  }
}

function emptyApi(): IntegrationApi {
  return {
    api_name: '',
    display_name: '',
    description: '',
    operation: 'add_record',
    obj_name: '',
    args: [],
    sync: true,
    active: true,
  }
}

/* ── Per-object model info (columns + actions) for the editors ────────── */

export interface ObjectColumn {
  db_name: string
  name: string
  heading: string
  col_type: string
  key: boolean
  mandatory: boolean
}

export interface ObjectModelInfo {
  loaded: boolean
  columns: ObjectColumn[]
  actions: string[]
}

function truthy(v: unknown): boolean {
  return ['true', '1', 'y', 'yes'].includes(String(v).toLowerCase())
}

function parseModelInfo(content: unknown): ObjectModelInfo {
  const model = (content || {}) as Record<string, unknown>
  const txn = ((model.transaction as Record<string, unknown>) || model) as Record<string, unknown>
  const sms = (txn.sql_models as unknown[]) || []
  const headerWrap = (sms[0] || {}) as Record<string, unknown>
  const header = ((headerWrap.sql_model as Record<string, unknown>) || headerWrap) as Record<string, unknown>
  const columns: ObjectColumn[] = []
  for (const wrap of (header.columns as unknown[]) || []) {
    const w = wrap as Record<string, unknown>
    const col = ((w && typeof w === 'object' && 'column' in w ? w.column : w) || {}) as Record<string, unknown>
    const dbName = String(col.db_name || col.name || '').trim()
    if (!dbName) continue
    columns.push({
      db_name: dbName,
      name: String(col.name || dbName),
      heading: String(col.heading || col.name || dbName),
      col_type: String(col.col_type || 'VARCHAR').toUpperCase(),
      key: truthy(col.key),
      mandatory: truthy(col.mandatory),
    })
  }
  const actions: string[] = []
  const pools: unknown[] = [
    ((txn.transaction as Record<string, unknown>) || {}).actions,
    txn.actions,
  ]
  for (const sm of sms) {
    const node = (((sm as Record<string, unknown>) || {}).sql_model || sm) as Record<string, unknown>
    pools.push(node?.actions)
  }
  for (const pool of pools) {
    for (const a of (pool as unknown[]) || []) {
      const name = a && typeof a === 'object' ? String((a as Record<string, unknown>).name || '').trim() : ''
      if (name && !actions.includes(name)) actions.push(name)
    }
  }
  return { loaded: true, columns, actions }
}

/** Cached loader of per-object designer-model summaries. */
function useObjectModels(folder: string) {
  const cacheRef = useRef(new Map<string, ObjectModelInfo>())
  const [version, setVersion] = useState(0)

  useEffect(() => {
    cacheRef.current.clear()
    setVersion((v) => v + 1)
  }, [folder])

  const load = useCallback(async (objName: string) => {
    const key = (objName || '').trim()
    if (!key || !folder || cacheRef.current.has(key)) return
    try {
      const res = await loadTransactionObject(folder, key)
      cacheRef.current.set(key, parseModelInfo(res?.content))
    } catch {
      cacheRef.current.set(key, { loaded: true, columns: [], actions: [] })
    }
    setVersion((v) => v + 1)
  }, [folder])

  const get = useCallback(
    (objName: string): ObjectModelInfo | undefined => cacheRef.current.get((objName || '').trim()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [version],
  )
  return { get, load }
}

/* ── Tab ──────────────────────────────────────────────────────────────── */

export default function IntegrationTab() {
  const { currentProject } = useProjectStore()
  const perms = useProjectPermissions()
  const folder = currentProject?.application.folder_name || ''

  const [design, setDesign] = useState<IntegrationDesign | null>(null)
  const [status, setStatus] = useState<IntegrationStatus | null>(null)
  // Flows tied to the project, as the Flows section last read them from IOFlow.
  const [projectFlows, setProjectFlows] = useState<ProjectFlow[]>([])
  const [loading, setLoading] = useState(true)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [syncReport, setSyncReport] = useState<SyncReport | null>(null)
  const [aiOpen, setAiOpen] = useState(false)
  const [aiDescription, setAiDescription] = useState('')
  const [generating, setGenerating] = useState(false)
  const [editingEvent, setEditingEvent] = useState<{ index: number; event: IntegrationEvent } | null>(null)
  const [editingApi, setEditingApi] = useState<{ index: number; api: IntegrationApi } | null>(null)

  const canEdit = perms.canEditObjects
  const models = useObjectModels(folder)

  /** Transaction objects (obj_type T) from the navigation tree. */
  const transactionObjects = useMemo(() => {
    const out: { obj_name: string; title: string }[] = []
    for (const group of currentProject?.navigation || []) {
      for (const obj of group.objects || []) {
        if (obj.type === 'T' && obj.obj_name) out.push({ obj_name: obj.obj_name, title: obj.name })
      }
      if (group.type === 'T' && group.obj_name) out.push({ obj_name: group.obj_name, title: group.name })
    }
    return out
  }, [currentProject])

  const objTitle = useCallback(
    (objName?: string) =>
      transactionObjects.find((t) => t.obj_name === objName)?.title || objName || '?',
    [transactionObjects],
  )

  const reload = useCallback(async () => {
    if (!folder) return
    setLoading(true)
    try {
      const [d, s] = await Promise.all([
        getIntegrationDesign(folder),
        getIntegrationStatus(folder, true).catch(() => null),
      ])
      setDesign(d)
      if (s) setStatus(s)
      setDirty(false)
    } catch (e) {
      console.error('Failed to load integration design', e)
      toast.error('Failed to load integration design')
    } finally {
      setLoading(false)
    }
  }, [folder])

  useEffect(() => {
    reload()
  }, [reload])

  const mutate = (fn: (d: IntegrationDesign) => IntegrationDesign) => {
    setDesign((d) => (d ? fn(d) : d))
    setDirty(true)
  }

  /** Open transaction designers reload when the save mirrored follow-ups. */
  const announceModelUpdates = (updated?: string[]) => {
    if (!updated || updated.length === 0) return
    window.dispatchEvent(
      new CustomEvent('twasta:agent-files-changed', {
        detail: { project: folder, objects: updated },
      }),
    )
  }

  const doSave = async (): Promise<boolean> => {
    if (!design || !folder) return false
    try {
      const res = await saveIntegrationDesign(folder, design)
      setDirty(false)
      announceModelUpdates(res.updated_models)
      return true
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'Failed to save integration design')
      return false
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      if (await doSave()) toast.success('Integration design saved')
    } finally {
      setSaving(false)
    }
  }

  const handleSync = async () => {
    if (!folder) return
    if (dirty && design && !(await doSave())) {
      toast.error('Save failed — fix the design before syncing')
      return
    }
    setSyncing(true)
    try {
      const report = await syncToIOFlow(folder)
      setSyncReport(report)
      if (report.success) {
        toast.success('Synced to IOFlow')
        announceModelUpdates(report.follow_ups?.updated_models)
        await reload()
      } else {
        toast.error(report.message || 'Nothing to sync')
      }
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'Sync to IOFlow failed')
    } finally {
      setSyncing(false)
    }
  }

  const handleOpenInIOFlow = async (
    target: 'application' | 'flows' | 'flow' | 'executions',
    flowId = '',
  ) => {
    try {
      const url = await getOpenInIOFlowUrl(folder, target, flowId)
      window.open(url, '_blank', 'noopener')
    } catch {
      toast.error('Could not build the IOFlow link — is IOFlow configured?')
    }
  }

  const handleGenerate = async () => {
    if (!aiDescription.trim() || aiDescription.trim().length < 10) {
      toast.error('Describe the events/APIs you need (at least 10 characters)')
      return
    }
    setGenerating(true)
    try {
      const res = await generateIntegrationDesign(folder, aiDescription)
      mutate((d) => {
        const existingEvents = new Set(d.events.map((e) => e.event_name.toLowerCase()))
        const existingApis = new Set(d.apis.map((a) => a.api_name.toLowerCase()))
        const newEvents = (res.events || []).filter(
          (e) => e.event_name && !existingEvents.has(e.event_name.toLowerCase()),
        )
        const newApis = (res.apis || []).filter(
          (a) => a.api_name && !existingApis.has(a.api_name.toLowerCase()),
        )
        toast.success(`AI added ${newEvents.length} event(s) and ${newApis.length} API(s)`)
        return { ...d, events: [...d.events, ...newEvents], apis: [...d.apis, ...newApis] }
      })
      setAiOpen(false)
      setAiDescription('')
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'AI generation failed')
    } finally {
      setGenerating(false)
    }
  }

  if (loading || !design) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <div className="spinner spinner-lg" />
      </div>
    )
  }

  const synced = Boolean(design.ioflow?.app_id)
  const inboundApis = design.apis.filter(isInboundApi)
  const legacyApis = design.apis.filter((a) => !isInboundApi(a))

  const removeApi = (apiDef: IntegrationApi) =>
    mutate((d) => ({ ...d, apis: d.apis.filter((a) => a !== apiDef) }))

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h2 style={{ margin: 0, fontSize: '1.3125rem' }}>Events &amp; APIs (IOFlow)</h2>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--color-text-secondary)' }}>
            <strong>Events</strong> are hooks this application calls in IOFlow when something
            happens here — flows subscribe and do the follow-up work. <strong>APIs</strong> are
            operations IOFlow flows can call on this application. Publish with{' '}
            <em>Sync to IOFlow</em>.
          </p>
          {synced && (
            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="badge badge-info">
                <span className="material-icons" style={{ fontSize: 15, marginRight: 4 }}>cloud_done</span>
                Synced {design.ioflow?.last_synced_at ? `· ${new Date(design.ioflow.last_synced_at).toLocaleString()}` : ''}
              </span>
              {dirty && <span className="badge">unsaved changes</span>}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {canEdit && (
            <button className="btn btn-secondary" onClick={() => setAiOpen(true)}>
              <span className="material-icons" style={{ fontSize: 19, marginRight: 4 }}>auto_awesome</span>
              Generate with AI
            </button>
          )}
          {canEdit && (
            <button className="btn btn-secondary" onClick={handleSave} disabled={saving || !dirty}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
          {canEdit && (
            <button className="btn btn-primary" onClick={handleSync} disabled={syncing}>
              {syncing ? (
                <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
              ) : (
                <span className="material-icons" style={{ fontSize: 19, marginRight: 4 }}>sync</span>
              )}
              Sync to IOFlow
            </button>
          )}
          {synced && (
            <button className="btn btn-secondary" onClick={() => handleOpenInIOFlow('application')}>
              <span className="material-icons" style={{ fontSize: 19, marginRight: 4 }}>open_in_new</span>
              Open in IOFlow
            </button>
          )}
        </div>
      </div>

      {/* Flows tied to the project (read live from IOFlow, both directions) */}
      <IntegrationFlowsSection
        project={folder}
        canEdit={canEdit}
        synced={synced}
        refreshKey={design.ioflow?.last_synced_at || ''}
        onOpen={(target, flowId) => handleOpenInIOFlow(target, flowId || '')}
        onSync={handleSync}
        onFlows={setProjectFlows}
      />

      {/* Events (Twasta ➜ IOFlow) */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.25rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.1125rem', flex: 1 }}>
            <span className="material-icons" style={{ fontSize: 21, verticalAlign: 'text-bottom', marginRight: 6, color: 'var(--color-accent)' }}>bolt</span>
            Events
            <span style={{ fontWeight: 400, fontSize: '0.8625rem', marginLeft: 8, color: 'var(--color-text-secondary)' }}>
              Twasta ➜ IOFlow
            </span>
          </h3>
          {canEdit && (
            <button className="btn btn-secondary" onClick={() => setEditingEvent({ index: -1, event: emptyEvent() })}>
              <span className="material-icons" style={{ fontSize: 17, marginRight: 4 }}>add</span>
              Add event
            </button>
          )}
        </div>
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
          Called when a record is added / edited / deleted (or an action runs) on an object,
          passing the selected fields as parameters. Each event appears as a follow-up action on
          its object and fires from there in preview, run and deployed applications.
        </p>
        {design.events.length === 0 ? (
          <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            No events yet — e.g. <em>order_created</em>, called whenever an Orders record is added,
            so an IOFlow flow can email the customer.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {design.events.map((ev, i) => {
              const flows = status?.flows_by_event?.[ev.event_name] || []
              const isTxn = ev.source?.type === 'transaction'
              const paramCount = (ev.params || []).length
              return (
                <div
                  key={`${ev.event_name}-${i}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.6rem 0.75rem', borderRadius: 8,
                    border: '1px solid var(--color-border)', flexWrap: 'wrap',
                  }}
                >
                  <span className="material-icons" style={{ fontSize: 19, color: ev.active ? 'var(--color-accent)' : 'var(--color-text-secondary)' }}>
                    bolt
                  </span>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontWeight: 600 }}>
                      {ev.display_name || ev.event_name}
                      <span style={{ fontFamily: 'monospace', fontWeight: 400, marginLeft: 8, fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
                        {ev.event_name}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
                      {isTxn
                        ? `${objTitle(ev.source.obj_name)} · ${TRIGGERS.find((t) => t.value === ev.source?.trigger)?.label || ev.source?.trigger}${ev.source?.trigger === 'action' && ev.source?.action_name ? ` (${ev.source.action_name})` : ''} · ${paramCount ? `${paramCount} field${paramCount === 1 ? '' : 's'}` : 'all fields'}`
                        : 'Custom event (emitted by design logic)'}
                    </div>
                  </div>
                  {!ev.active && <span className="badge">inactive</span>}
                  {design.ioflow?.event_ids?.[ev.event_name] ? (
                    <span className="badge badge-info">synced</span>
                  ) : (
                    <span className="badge">not synced</span>
                  )}
                  {flows.map((f) => (
                    <button
                      key={f.flow_id}
                      className="btn btn-secondary"
                      style={{ padding: '2px 8px', fontSize: '0.8125rem' }}
                      title={`Open flow "${f.flow_name}" in IOFlow`}
                      onClick={() => handleOpenInIOFlow('flow', f.flow_id)}
                    >
                      <span className="material-icons" style={{ fontSize: 15, marginRight: 2 }}>account_tree</span>
                      {f.flow_name}
                    </button>
                  ))}
                  {canEdit && (
                    <>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '2px 8px' }}
                        onClick={() => setEditingEvent({ index: i, event: { ...ev, source: { ...ev.source }, params: [...(ev.params || [])] } })}
                      >
                        <span className="material-icons" style={{ fontSize: 17 }}>edit</span>
                      </button>
                      <button
                        className="btn btn-danger"
                        style={{ padding: '2px 8px' }}
                        onClick={() => mutate((d) => ({ ...d, events: d.events.filter((_, j) => j !== i) }))}
                      >
                        <span className="material-icons" style={{ fontSize: 17 }}>delete</span>
                      </button>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* APIs (IOFlow ➜ Twasta) */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.25rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.1125rem', flex: 1 }}>
            <span className="material-icons" style={{ fontSize: 21, verticalAlign: 'text-bottom', marginRight: 6, color: 'var(--color-accent)' }}>api</span>
            APIs
            <span style={{ fontWeight: 400, fontSize: '0.8625rem', marginLeft: 8, color: 'var(--color-text-secondary)' }}>
              IOFlow ➜ Twasta
            </span>
          </h3>
          {canEdit && (
            <button className="btn btn-secondary" onClick={() => setEditingApi({ index: -1, api: emptyApi() })}>
              <span className="material-icons" style={{ fontSize: 17, marginRight: 4 }}>add</span>
              Add API
            </button>
          )}
        </div>
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
          Operations IOFlow flows can call on this application — add or update a record, read
          records, or run a transaction action (e.g. approve / cancel). Each API clearly defines
          the arguments it needs and is published to IOFlow as a callable service.
        </p>
        {inboundApis.length === 0 ? (
          <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            No APIs yet — e.g. <em>add_order</em> lets a flow create an Orders record, or{' '}
            <em>approve_order</em> lets it run the Approve action on one.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {inboundApis.map((apiDef) => {
              const op = OPERATIONS.find((o) => o.value === apiDef.operation)
              const argCount = (apiDef.args || []).length
              const i = design.apis.indexOf(apiDef)
              return (
                <div
                  key={`${apiDef.api_name}-${i}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.6rem 0.75rem', borderRadius: 8,
                    border: '1px solid var(--color-border)', flexWrap: 'wrap',
                  }}
                >
                  <span className="material-icons" style={{ fontSize: 19, color: apiDef.active ? 'var(--color-accent)' : 'var(--color-text-secondary)' }}>
                    {op?.icon || 'api'}
                  </span>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontWeight: 600 }}>
                      {apiDef.display_name || apiDef.api_name}
                      <span style={{ fontFamily: 'monospace', fontWeight: 400, marginLeft: 8, fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
                        {apiDef.api_name}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
                      {op?.label || apiDef.operation} · {objTitle(apiDef.obj_name)}
                      {apiDef.operation === 'run_action' && apiDef.action_name ? ` · action: ${apiDef.action_name}` : ''}
                      {apiDef.operation !== 'run_action'
                        ? ` · ${argCount ? `${argCount} field${argCount === 1 ? '' : 's'}` : 'all fields'}`
                        : ''}
                    </div>
                  </div>
                  {!apiDef.active && <span className="badge">inactive</span>}
                  {(() => {
                    const users = projectFlows.filter((f) =>
                      f.uses.some((u) => u.kind === 'api' && u.name === apiDef.api_name),
                    )
                    if (!users.length) return null
                    return (
                      <span
                        className="badge badge-info"
                        title={`Called by: ${users.map((f) => f.name).join(', ')}`}
                      >
                        {users.length === 1 ? '1 flow' : `${users.length} flows`}
                      </span>
                    )
                  })()}
                  {design.ioflow?.service_ids?.[apiDef.api_name] ? (
                    <span className="badge badge-info">synced</span>
                  ) : (
                    <span className="badge">not synced</span>
                  )}
                  {canEdit && (
                    <>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '2px 8px' }}
                        onClick={() => setEditingApi({ index: i, api: { ...apiDef, args: [...(apiDef.args || [])] } })}
                      >
                        <span className="material-icons" style={{ fontSize: 17 }}>edit</span>
                      </button>
                      <button
                        className="btn btn-danger"
                        style={{ padding: '2px 8px' }}
                        onClick={() => removeApi(apiDef)}
                      >
                        <span className="material-icons" style={{ fontSize: 17 }}>delete</span>
                      </button>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Legacy outbound APIs — read-only leftovers from the old model */}
      {legacyApis.length > 0 && (
        <div className="card" style={{ marginBottom: '1.25rem' }}>
          <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.1125rem' }}>
            <span className="material-icons" style={{ fontSize: 21, verticalAlign: 'text-bottom', marginRight: 6, color: 'var(--color-text-secondary)' }}>history</span>
            Legacy outbound APIs
          </h3>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
            Older "app calls IOFlow" definitions (send email / WhatsApp / raw URL calls). They keep
            working for existing Call-API logic, but new designs should define an <strong>event</strong>{' '}
            instead and let the IOFlow flow do the sending. Deleting one breaks Call-API specs that
            reference it.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {legacyApis.map((apiDef) => {
              const i = design.apis.indexOf(apiDef)
              return (
                <div
                  key={`${apiDef.api_name}-${i}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.6rem 0.75rem', borderRadius: 8,
                    border: '1px dashed var(--color-border)', flexWrap: 'wrap',
                  }}
                >
                  <span className="material-icons" style={{ fontSize: 19, color: 'var(--color-text-secondary)' }}>outbound</span>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontWeight: 600 }}>
                      {apiDef.display_name || apiDef.api_name}
                      <span style={{ fontFamily: 'monospace', fontWeight: 400, marginLeft: 8, fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
                        {apiDef.api_name}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
                      {LEGACY_CATEGORY_LABELS[apiDef.category || ''] || apiDef.category}
                      {apiDef.migrated_from ? ` · migrated from ${apiDef.migrated_from}` : ''}
                    </div>
                  </div>
                  {!apiDef.active && <span className="badge">inactive</span>}
                  {canEdit && (
                    <button className="btn btn-danger" style={{ padding: '2px 8px' }} onClick={() => removeApi(apiDef)}>
                      <span className="material-icons" style={{ fontSize: 17 }}>delete</span>
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Event editor modal */}
      {editingEvent && (
        <EventEditorModal
          initial={editingEvent.event}
          isNew={editingEvent.index === -1}
          transactionObjects={transactionObjects}
          models={models}
          existingNames={design.events.map((e, i) => (i === editingEvent.index ? '' : e.event_name.toLowerCase()))}
          onClose={() => setEditingEvent(null)}
          onSave={(ev) => {
            mutate((d) => {
              const events = [...d.events]
              if (editingEvent.index === -1) events.push(ev)
              else events[editingEvent.index] = ev
              return { ...d, events }
            })
            setEditingEvent(null)
          }}
        />
      )}

      {/* API editor modal */}
      {editingApi && (
        <ApiEditorModal
          initial={editingApi.api}
          isNew={editingApi.index === -1}
          transactionObjects={transactionObjects}
          models={models}
          inboundUrlTemplate={status?.inbound_call_url_template || ''}
          existingNames={design.apis.map((a, i) => (i === editingApi.index ? '' : a.api_name.toLowerCase()))}
          onClose={() => setEditingApi(null)}
          onSave={(apiDef) => {
            mutate((d) => {
              const apis = [...d.apis]
              if (editingApi.index === -1) apis.push(apiDef)
              else apis[editingApi.index] = apiDef
              return { ...d, apis }
            })
            setEditingApi(null)
          }}
        />
      )}

      {/* AI generation modal */}
      <Modal open={aiOpen} onClose={() => setAiOpen(false)} title="Generate events & APIs with AI">
        <p style={{ marginTop: 0, color: 'var(--color-text-secondary)' }}>
          Describe the integrations you need; the AI proposes events (bound to your real
          transactions) and inbound APIs, which are added to the lists for review.
        </p>
        <textarea
          className="input"
          rows={5}
          style={{ width: '100%', resize: 'vertical' }}
          placeholder='e.g. "Call an event whenever a sales order is created or approved (pass order no, customer and total), and let IOFlow create purchase orders and approve them."'
          value={aiDescription}
          onChange={(e) => setAiDescription(e.target.value)}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
          <button className="btn btn-secondary" onClick={() => setAiOpen(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleGenerate} disabled={generating}>
            {generating ? (
              <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
            ) : (
              <span className="material-icons" style={{ fontSize: 19, marginRight: 4 }}>auto_awesome</span>
            )}
            Generate
          </button>
        </div>
      </Modal>

      {/* Sync report modal */}
      <Modal open={!!syncReport} onClose={() => setSyncReport(null)} title="IOFlow sync result">
        {syncReport && (
          <div style={{ fontSize: '0.9625rem' }}>
            {syncReport.success ? (
              <>
                <p style={{ marginTop: 0 }}>
                  <span className="material-icons" style={{ fontSize: 19, color: 'var(--color-success, #4caf50)', verticalAlign: 'text-bottom', marginRight: 6 }}>check_circle</span>
                  Application <strong>{syncReport.app_name}</strong> synced.
                </p>
                <ul style={{ margin: '0.5rem 0', paddingLeft: '1.25rem' }}>
                  <li>{syncReport.events_synced?.length || 0} event(s) published</li>
                  <li>
                    {syncReport.inbound_apis_synced?.length || 0} API(s) published as callable services
                  </li>
                  {(syncReport.follow_ups?.updated_models?.length || 0) > 0 && (
                    <li>
                      Follow-up actions updated on:{' '}
                      {syncReport.follow_ups!.updated_models.join(', ')}
                    </li>
                  )}
                  <li>
                    Invocation key {syncReport.has_invocation_key ? 'stored — runtime delivery is enabled' : 'NOT available'}
                  </li>
                </ul>
                {syncReport.warning && (
                  <p style={{ color: 'var(--color-warning, #ff9800)' }}>
                    <span className="material-icons" style={{ fontSize: 17, verticalAlign: 'text-bottom', marginRight: 4 }}>warning</span>
                    {syncReport.warning}
                  </p>
                )}
              </>
            ) : (
              <p style={{ marginTop: 0 }}>{syncReport.message || 'Nothing was synced.'}</p>
            )}
            {syncReport.migration && syncReport.migration.migrated.length > 0 && (
              <>
                <h4 style={{ margin: '0.75rem 0 0.25rem' }}>Auto-migrated raw URL calls</h4>
                <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                  {syncReport.migration.migrated.map((m) => (
                    <li key={m.api_name}>
                      <code>{m.url}</code> → <code>{m.api_name}</code>
                    </li>
                  ))}
                </ul>
                {syncReport.migration.rewritten_files.length > 0 && (
                  <p style={{ color: 'var(--color-text-secondary)' }}>
                    Updated {syncReport.migration.rewritten_files.length} model file(s) to route through IOFlow.
                  </p>
                )}
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              {syncReport.success && (
                <button className="btn btn-secondary" onClick={() => handleOpenInIOFlow('application')}>
                  <span className="material-icons" style={{ fontSize: 17, marginRight: 4 }}>open_in_new</span>
                  Open in IOFlow
                </button>
              )}
              <button className="btn btn-primary" onClick={() => setSyncReport(null)}>Close</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

/* ── Shared field-selection list (event params / API args) ────────────── */

function FieldChecklist({
  columns, selected, onChange, allLabel, emptyHint,
}: {
  columns: ObjectColumn[]
  selected: string[]
  onChange: (next: string[]) => void
  allLabel: string
  emptyHint: string
}) {
  const passAll = selected.length === 0
  if (columns.length === 0) {
    return (
      <p style={{ margin: 0, fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
        {emptyHint}
      </p>
    )
  }
  const toggle = (dbName: string) => {
    const base = passAll ? columns.map((c) => c.db_name) : selected
    const next = base.includes(dbName) ? base.filter((n) => n !== dbName) : [...base, dbName]
    // Selecting every field collapses back to "all" ([]), keeping the design
    // robust when columns are added to the object later.
    onChange(next.length === columns.length ? [] : next)
  }
  return (
    <div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: '0.9125rem' }}>
        <input
          type="checkbox"
          checked={passAll}
          onChange={(e) => onChange(e.target.checked ? [] : columns.map((c) => c.db_name))}
        />
        {allLabel}
      </label>
      <div
        style={{
          maxHeight: 180, overflow: 'auto', border: '1px solid var(--color-border)',
          borderRadius: 6, padding: '0.4rem 0.6rem',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2,
          opacity: passAll ? 0.75 : 1,
        }}
      >
        {columns.map((c) => {
          const checked = passAll || selected.includes(c.db_name)
          return (
            <label key={c.db_name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8625rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={checked} onChange={() => toggle(c.db_name)} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.heading}
                <span style={{ fontFamily: 'monospace', color: 'var(--color-text-secondary)', marginLeft: 6 }}>
                  {c.db_name} · {c.col_type.toLowerCase()}{c.mandatory ? ' · required' : ''}
                </span>
              </span>
            </label>
          )
        })}
      </div>
    </div>
  )
}

/* ── Event arguments editor ───────────────────────────────────────── */

const ARG_TYPES: IntegrationArgType[] = [
  'string', 'number', 'integer', 'boolean', 'date', 'datetime', 'object', 'array',
]

function colToArgType(colType: string): IntegrationArgType {
  switch ((colType || '').toUpperCase()) {
    case 'NUMBER': return 'number'
    case 'DATE': return 'date'
    case 'DATETIME': return 'datetime'
    case 'BOOLEAN': return 'boolean'
    default: return 'string'
  }
}

/**
 * Per-argument editor — the TYPED inputs the IOFlow flow receives and maps.
 * Authoritative for custom (BL-triggered) events, which have no record columns
 * to derive a schema from. For transaction events each argument may name a
 * `source` column the value comes from.
 */
function ArgumentsEditor({
  args, columns, isTxn, params, onChange,
}: {
  args: IntegrationEventArg[]
  columns: ObjectColumn[]
  isTxn: boolean
  params: string[]
  onChange: (next: IntegrationEventArg[]) => void
}) {
  const update = (i: number, patch: Partial<IntegrationEventArg>) =>
    onChange(args.map((a, idx) => (idx === i ? { ...a, ...patch } : a)))
  const add = () => onChange([...args, { name: '', type: 'string', required: false }])
  const remove = (i: number) => onChange(args.filter((_, idx) => idx !== i))
  const deriveFromParams = () => {
    const cols = params.length
      ? columns.filter((c) => params.includes(c.db_name))
      : columns
    onChange(cols.map((c) => ({
      name: c.db_name.toLowerCase(),
      type: colToArgType(c.col_type),
      required: !!c.mandatory,
      source: c.db_name,
    })))
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <div style={{ fontSize: '0.8625rem' }}>
          Arguments passed to IOFlow (the typed inputs a flow maps to)
        </div>
        {isTxn && columns.length > 0 && (
          <button
            type="button"
            className="btn btn-secondary"
            style={{ fontSize: '0.8125rem', padding: '0.15rem 0.5rem' }}
            onClick={deriveFromParams}
          >
            Derive from selected fields
          </button>
        )}
      </div>
      {args.length === 0 ? (
        <p style={{ margin: '0 0 6px', fontSize: '0.8425rem', color: 'var(--color-text-secondary)' }}>
          No arguments declared yet. {isTxn
            ? 'Declare the typed inputs the flow needs, or derive them from the selected fields.'
            : 'A custom event must declare its arguments — otherwise IOFlow receives nothing to map.'}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {args.map((a, i) => (
            <div
              key={i}
              style={{
                display: 'grid',
                gridTemplateColumns: isTxn
                  ? '1.2fr 0.9fr 1.4fr 1fr auto auto'
                  : '1.2fr 0.9fr 1.6fr auto auto',
                gap: 6, alignItems: 'center',
              }}
            >
              <input
                className="input"
                placeholder="name"
                value={a.name}
                onChange={(e) => update(i, { name: e.target.value })}
                style={{ fontFamily: 'monospace', fontSize: '0.8425rem' }}
              />
              <select
                className="input"
                value={a.type}
                onChange={(e) => update(i, { type: e.target.value as IntegrationArgType })}
                style={{ fontSize: '0.8425rem' }}
              >
                {ARG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input
                className="input"
                placeholder="description"
                value={a.description || ''}
                onChange={(e) => update(i, { description: e.target.value })}
                style={{ fontSize: '0.8425rem' }}
              />
              {isTxn && (
                <select
                  className="input"
                  value={a.source || ''}
                  onChange={(e) => update(i, { source: e.target.value })}
                  style={{ fontSize: '0.8425rem' }}
                  title="Source field"
                >
                  <option value="">— source field —</option>
                  {columns.map((c) => (
                    <option key={c.db_name} value={c.db_name}>{c.db_name}</option>
                  ))}
                </select>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8125rem' }} title="Required">
                <input
                  type="checkbox"
                  checked={!!a.required}
                  onChange={(e) => update(i, { required: e.target.checked })}
                />
                req
              </label>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: '0.8125rem', padding: '0.15rem 0.4rem' }}
                onClick={() => remove(i)}
                title="Remove argument"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        className="btn btn-secondary"
        style={{ fontSize: '0.8125rem', padding: '0.2rem 0.6rem', marginTop: 6 }}
        onClick={add}
      >
        + Add argument
      </button>
    </div>
  )
}

/* ── Event editor ─────────────────────────────────────────────────── */

function EventEditorModal({
  initial, isNew, transactionObjects, models, existingNames, onClose, onSave,
}: {
  initial: IntegrationEvent
  isNew: boolean
  transactionObjects: { obj_name: string; title: string }[]
  models: { get: (obj: string) => ObjectModelInfo | undefined; load: (obj: string) => Promise<void> }
  existingNames: string[]
  onClose: () => void
  onSave: (ev: IntegrationEvent) => void
}) {
  const [ev, setEv] = useState<IntegrationEvent>(initial)
  const isTxn = ev.source.type === 'transaction'
  const objName = ev.source.obj_name || ''
  const info = models.get(objName)

  useEffect(() => {
    if (objName) models.load(objName)
  }, [objName, models])

  const set = (patch: Partial<IntegrationEvent>) => setEv((e) => ({ ...e, ...patch }))
  const setSource = (patch: Partial<IntegrationEvent['source']>) =>
    setEv((e) => ({ ...e, source: { ...e.source, ...patch } }))

  const suggestName = (source: IntegrationEvent['source']) => {
    if (source.type !== 'transaction' || !source.obj_name) return ''
    const trig = source.trigger === 'action' ? (source.action_name || 'action') : source.trigger
    return `${slugify(source.obj_name)}_${slugify(trig || '')}`
  }

  const handleSave = () => {
    const name = ev.event_name.trim()
    if (!name) {
      toast.error('Event name is required')
      return
    }
    if (!/^[a-z][a-z0-9_.]*$/i.test(name)) {
      toast.error('Event name must be alphanumeric/underscore (e.g. order_created)')
      return
    }
    if (existingNames.includes(name.toLowerCase())) {
      toast.error('An event with this name already exists')
      return
    }
    if (isTxn && !ev.source.obj_name) {
      toast.error('Pick the transaction object')
      return
    }
    if (isTxn && ev.source.trigger === 'action' && !(ev.source.action_name || '').trim()) {
      toast.error('Pick the action that calls this event')
      return
    }
    const argList = ev.arguments || []
    const argNames = new Set<string>()
    for (const a of argList) {
      const an = (a.name || '').trim()
      if (!an) {
        toast.error('Every argument needs a name (or remove the empty row)')
        return
      }
      if (argNames.has(an.toLowerCase())) {
        toast.error(`Duplicate argument name: ${an}`)
        return
      }
      argNames.add(an.toLowerCase())
    }
    // Drop empty-description noise; keep declared order.
    const cleanedArgs = argList.map((a) => ({ ...a, name: a.name.trim() }))
    onSave({ ...ev, event_name: name, arguments: cleanedArgs })
  }

  const trigger = ev.source.trigger || 'add'

  /** Switch what fires this event — a record change/action, or a BL `emit_event` call. */
  const setSourceType = (type: 'transaction' | 'custom') => {
    if (type === ev.source.type) return
    setEv((cur) =>
      type === 'custom'
        ? { ...cur, source: { type: 'custom' }, params: [] }
        : {
            ...cur,
            source: {
              type: 'transaction',
              obj_name: cur.source.obj_name || '',
              trigger: cur.source.trigger || 'add',
              action_name: cur.source.action_name || '',
            },
          },
    )
  }

  // Business-logic call that fires a custom event — shown so the design and the
  // BL that emits it stay in lock-step (argument names must match the payload keys).
  const emitArgs = (ev.arguments || []).filter((a) => (a.name || '').trim())
  const emitPayload = emitArgs.length
    ? `{ ${emitArgs.map((a) => `"${a.name.trim()}": …`).join(', ')} }`
    : '{ … }'
  const emitSnippet = `api.emit_event("${ev.event_name.trim() || 'event_name'}", ${emitPayload})`

  return (
    <Modal open onClose={onClose} title={isNew ? 'Add event' : 'Edit event'}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {/* What fires this event — mirrors the two source.type kinds the runtime supports. */}
        <div>
          <div style={{ fontSize: '0.8625rem', marginBottom: 4 }}>How is this event triggered?</div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {([
              { type: 'transaction' as const, icon: 'bolt', label: 'Record change / action',
                hint: 'Fires when a record is added, edited, deleted, or an action runs.' },
              { type: 'custom' as const, icon: 'code', label: 'Business logic (custom)',
                hint: 'Fired from BL code with api.emit_event(name, payload).' },
            ]).map((opt) => {
              const selected = (ev.source.type || 'transaction') === opt.type
              return (
                <button
                  key={opt.type}
                  type="button"
                  className={selected ? 'btn btn-primary' : 'btn btn-secondary'}
                  style={{ flex: 1, minWidth: 180, height: 'auto', flexDirection: 'column', alignItems: 'flex-start', gap: 2, textAlign: 'left', padding: '0.5rem 0.75rem', whiteSpace: 'normal', lineHeight: 1.3 }}
                  onClick={() => setSourceType(opt.type)}
                  title={opt.hint}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                    <span className="material-icons" style={{ fontSize: 17 }}>{opt.icon}</span>
                    {opt.label}
                  </span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 400, opacity: 0.85 }}>{opt.hint}</span>
                </button>
              )
            })}
          </div>
        </div>
        {!isTxn ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <p style={{ margin: 0, fontSize: '0.9125rem', color: 'var(--color-text-secondary)' }}>
              Custom event — emitted by your business-logic code rather than a record change. Declare
              its arguments below (they must match the payload the BL passes), then call it from any
              item-change / validation / action function:
            </p>
            <code
              style={{
                display: 'block', padding: '0.5rem 0.6rem', borderRadius: 6,
                border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary, rgba(127,127,127,0.08))',
                fontSize: '0.8425rem', wordBreak: 'break-all',
              }}
            >
              {emitSnippet}
            </code>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: 2, minWidth: 220 }}>
                <ObjectPickerField
                  label="When this happens on"
                  value={objName}
                  onChange={(next) => {
                    const source = { ...ev.source, obj_name: next, action_name: '' }
                    setEv((cur) => ({
                      ...cur,
                      source,
                      params: [],
                      event_name: cur.event_name || suggestName(source),
                    }))
                  }}
                  catalog={transactionObjects.map((t) => ({ obj_name: t.obj_name, title: t.title }))}
                  typeLabel="transaction"
                  placeholder="Pick the transaction object…"
                  allowCustom={false}
                  emptyHint="No transaction objects in this project yet."
                />
              </div>
              <label style={{ flex: 1, minWidth: 170 }}>
                <div style={{ fontSize: '0.8625rem', marginBottom: 4 }}>Trigger</div>
                <select
                  className="input"
                  value={trigger}
                  onChange={(e) => {
                    const source = { ...ev.source, trigger: e.target.value as IntegrationEvent['source']['trigger'] }
                    setEv((cur) => ({
                      ...cur,
                      source,
                      event_name: cur.event_name || suggestName(source),
                    }))
                  }}
                >
                  {TRIGGERS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </label>
            </div>
            {trigger === 'action' && (
              <label>
                <div style={{ fontSize: '0.8625rem', marginBottom: 4 }}>Action (defined on the transaction)</div>
                <select
                  className="input"
                  value={ev.source.action_name || ''}
                  onChange={(e) => {
                    const source = { ...ev.source, action_name: e.target.value }
                    setEv((cur) => ({
                      ...cur,
                      source,
                      event_name: cur.event_name || suggestName(source),
                    }))
                  }}
                  disabled={!objName}
                >
                  <option value="">{objName ? (info?.loaded && info.actions.length === 0 ? '— no actions on this object —' : '— select action —') : '— pick the object first —'}</option>
                  {(info?.actions || []).map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </label>
            )}
            <div>
              <div style={{ fontSize: '0.8625rem', marginBottom: 4 }}>
                Parameters passed to IOFlow (the event's <code>record</code> payload)
              </div>
              {!objName ? (
                <p style={{ margin: 0, fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
                  Pick the transaction object to choose its fields.
                </p>
              ) : !info ? (
                <p style={{ margin: 0, fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>Loading fields…</p>
              ) : (
                <FieldChecklist
                  columns={info.columns}
                  selected={ev.params || []}
                  onChange={(params) => set({ params })}
                  allLabel="Pass all fields of the record"
                  emptyHint="This object's designer model has no fields yet — the full record will be passed."
                />
              )}
            </div>
          </>
        )}
        {/* Typed arguments — shown for BOTH custom and transaction events. For a
            custom (BL-triggered) event this is the ONLY way IOFlow learns its
            inputs. */}
        <ArgumentsEditor
          args={ev.arguments || []}
          columns={isTxn ? (info?.columns || []) : []}
          isTxn={isTxn}
          params={ev.params || []}
          onChange={(args) => set({ arguments: args })}
        />
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <label style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: '0.8625rem', marginBottom: 4 }}>Event name (identifier)</div>
            <input
              className="input"
              value={ev.event_name}
              onChange={(e) => set({ event_name: e.target.value })}
              placeholder="order_created"
              style={{ fontFamily: 'monospace' }}
            />
          </label>
          <label style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: '0.8625rem', marginBottom: 4 }}>Display name</div>
            <input
              className="input"
              value={ev.display_name || ''}
              onChange={(e) => set({ display_name: e.target.value })}
              placeholder="Order Created"
            />
          </label>
        </div>
        <label>
          <div style={{ fontSize: '0.8625rem', marginBottom: 4 }}>Description (what should the IOFlow flow do?)</div>
          <textarea
            className="input"
            rows={2}
            style={{ width: '100%', resize: 'vertical' }}
            value={ev.description || ''}
            onChange={(e) => set({ description: e.target.value })}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={ev.active}
            onChange={(e) => set({ active: e.target.checked })}
          />
          Active (called at runtime)
        </label>
        {isTxn && objName && trigger !== 'action' && (
          <p style={{ margin: 0, fontSize: '0.8425rem', color: 'var(--color-text-secondary)' }}>
            <span className="material-icons" style={{ fontSize: 15, verticalAlign: 'text-bottom', marginRight: 4 }}>info</span>
            Saving adds a follow-up action on <strong>{objName}</strong> that calls this event —
            you'll see it in the transaction designer under Follow-up Actions.
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave}>
            {isNew ? 'Add event' : 'Apply'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

/* ── API editor ───────────────────────────────────────────────────── */

function ApiEditorModal({
  initial, isNew, transactionObjects, models, inboundUrlTemplate, existingNames, onClose, onSave,
}: {
  initial: IntegrationApi
  isNew: boolean
  transactionObjects: { obj_name: string; title: string }[]
  models: { get: (obj: string) => ObjectModelInfo | undefined; load: (obj: string) => Promise<void> }
  inboundUrlTemplate: string
  existingNames: string[]
  onClose: () => void
  onSave: (apiDef: IntegrationApi) => void
}) {
  const [apiDef, setApiDef] = useState<IntegrationApi>({ ...initial, operation: initial.operation || 'add_record' })
  const objName = apiDef.obj_name || ''
  const info = models.get(objName)
  const operation = apiDef.operation || 'add_record'

  useEffect(() => {
    if (objName) models.load(objName)
  }, [objName, models])

  const set = (patch: Partial<IntegrationApi>) => setApiDef((a) => ({ ...a, ...patch }))

  const suggestName = (op: IntegrationApiOperation, obj: string, action = '') => {
    if (!obj) return ''
    const base = slugify(obj)
    if (op === 'add_record') return `add_${base}`
    if (op === 'update_record') return `update_${base}`
    if (op === 'get_records') return `get_${base}`
    if (op === 'bulk_upsert') return `upsert_${base}`
    return action ? `${slugify(action)}_${base}` : ''
  }

  const handleSave = () => {
    const name = apiDef.api_name.trim()
    if (!name) {
      toast.error('API name is required')
      return
    }
    if (!/^[a-z][a-z0-9_]*$/i.test(name)) {
      toast.error('API name must be alphanumeric/underscore (e.g. add_order)')
      return
    }
    if (existingNames.includes(name.toLowerCase())) {
      toast.error('An API with this name already exists')
      return
    }
    if (!objName) {
      toast.error('Pick the transaction object')
      return
    }
    if (operation === 'run_action' && !(apiDef.action_name || '').trim()) {
      toast.error('Pick the action this API runs')
      return
    }
    onSave({ ...apiDef, api_name: name, operation, sync: true })
  }

  const opMeta = OPERATIONS.find((o) => o.value === operation)
  const endpoint = inboundUrlTemplate
    ? inboundUrlTemplate.replace('{api_name}', apiDef.api_name.trim() || '<api_name>')
    : ''
  const needsTxnId = operation === 'update_record' || operation === 'run_action'
  // A bulk upsert matches on the key fields, so a field list that leaves one
  // out can never find an existing record: every call would only add.
  const missingKeys = operation === 'bulk_upsert' && info && (apiDef.args || []).length > 0
    ? info.columns.filter((c) => c.key && !(apiDef.args || []).some(
        (a) => a.toLowerCase() === c.db_name.toLowerCase() || a.toLowerCase() === c.name.toLowerCase()))
    : []

  return (
    <Modal open onClose={onClose} title={isNew ? 'Add API' : 'Edit API'}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: '0.8625rem', marginBottom: 4 }}>Operation</div>
            <select
              className="input"
              value={operation}
              onChange={(e) => {
                const op = e.target.value as IntegrationApiOperation
                setApiDef((a) => ({
                  ...a,
                  operation: op,
                  action_name: op === 'run_action' ? a.action_name : '',
                  api_name: a.api_name || suggestName(op, a.obj_name || '', a.action_name || ''),
                }))
              }}
            >
              {OPERATIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <div style={{ flex: 2, minWidth: 220 }}>
            <ObjectPickerField
              label="Transaction"
              value={objName}
              onChange={(next) =>
                setApiDef((a) => ({
                  ...a,
                  obj_name: next,
                  action_name: '',
                  args: [],
                  api_name: a.api_name || suggestName(operation, next),
                }))
              }
              catalog={transactionObjects.map((t) => ({ obj_name: t.obj_name, title: t.title }))}
              typeLabel="transaction"
              placeholder="Pick the transaction object…"
              allowCustom={false}
              emptyHint="No transaction objects in this project yet."
            />
          </div>
        </div>
        {opMeta && (
          <p style={{ margin: 0, fontSize: '0.8425rem', color: 'var(--color-text-secondary)' }}>
            {opMeta.hint}
          </p>
        )}
        {operation === 'run_action' && (
          <label>
            <div style={{ fontSize: '0.8625rem', marginBottom: 4 }}>Action to run</div>
            <select
              className="input"
              value={apiDef.action_name || ''}
              onChange={(e) =>
                setApiDef((a) => ({
                  ...a,
                  action_name: e.target.value,
                  api_name: a.api_name || suggestName('run_action', a.obj_name || '', e.target.value),
                }))
              }
              disabled={!objName}
            >
              <option value="">{objName ? (info?.loaded && info.actions.length === 0 ? '— no actions on this object —' : '— select action —') : '— pick the object first —'}</option>
              {(info?.actions || []).map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </label>
        )}
        {/* Arguments */}
        <div>
          <div style={{ fontSize: '0.8625rem', marginBottom: 4 }}>Arguments</div>
          {needsTxnId && (
            <p style={{ margin: '0 0 6px', fontSize: '0.8625rem' }}>
              <code>txn_id</code>
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {' '}· string · required — the id of the record to {operation === 'run_action' ? 'run the action on' : 'update'}
              </span>
            </p>
          )}
          {operation === 'bulk_upsert' && (
            <p style={{ margin: '0 0 6px', fontSize: '0.8625rem' }}>
              <code>records</code>
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {' '}· list · required — each record carries the fields below; </span>
              <code>ignore_warnings</code>
              <span style={{ color: 'var(--color-text-secondary)' }}> · list · optional — warning codes to accept</span>
            </p>
          )}
          {missingKeys.length > 0 && (
            <p style={{ margin: '0 0 6px', fontSize: '0.8625rem', color: 'var(--color-warning)' }}>
              Include the key field{missingKeys.length > 1 ? 's' : ''}{' '}
              {missingKeys.map((c) => c.db_name).join(', ')} — without {missingKeys.length > 1 ? 'them' : 'it'} no
              existing record can be matched, so every record would be added.
            </p>
          )}
          {operation === 'get_records' && (
            <p style={{ margin: '0 0 6px', fontSize: '0.8625rem' }}>
              <code>limit</code>, <code>offset</code>
              <span style={{ color: 'var(--color-text-secondary)' }}> · integer · optional — paging</span>
            </p>
          )}
          {!objName ? (
            <p style={{ margin: 0, fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
              Pick the transaction object to define the field arguments.
            </p>
          ) : !info ? (
            <p style={{ margin: 0, fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>Loading fields…</p>
          ) : operation === 'run_action' ? (
            (apiDef.args || []).length > 0 || info.columns.length > 0 ? (
              <FieldChecklist
                columns={info.columns}
                selected={apiDef.args || []}
                onChange={(args) => set({ args })}
                allLabel="Also accept all record fields as overrides (advanced)"
                emptyHint="No extra field arguments — the action runs on the loaded record."
              />
            ) : null
          ) : (
            <FieldChecklist
              columns={info.columns}
              selected={apiDef.args || []}
              onChange={(args) => set({ args })}
              allLabel={operation === 'get_records' ? 'Allow filtering by any field' : 'Accept all enterable fields'}
              emptyHint="This object's designer model has no fields yet."
            />
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <label style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: '0.8625rem', marginBottom: 4 }}>API name (identifier)</div>
            <input
              className="input"
              value={apiDef.api_name}
              onChange={(e) => set({ api_name: e.target.value })}
              placeholder="add_order"
              style={{ fontFamily: 'monospace' }}
            />
          </label>
          <label style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: '0.8625rem', marginBottom: 4 }}>Display name</div>
            <input
              className="input"
              value={apiDef.display_name || ''}
              onChange={(e) => set({ display_name: e.target.value })}
              placeholder="Add Order"
            />
          </label>
        </div>
        <label>
          <div style={{ fontSize: '0.8625rem', marginBottom: 4 }}>Description</div>
          <textarea
            className="input"
            rows={2}
            style={{ width: '100%', resize: 'vertical' }}
            value={apiDef.description || ''}
            onChange={(e) => set({ description: e.target.value })}
            placeholder="What does calling this API do in the application?"
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={apiDef.active}
            onChange={(e) => set({ active: e.target.checked })}
          />
          Active (callable from IOFlow)
        </label>
        {endpoint ? (
          <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--color-text-secondary)', wordBreak: 'break-all' }}>
            <span className="material-icons" style={{ fontSize: 15, verticalAlign: 'text-bottom', marginRight: 4 }}>link</span>
            Published to IOFlow as a service calling <code>POST {endpoint}</code>
          </p>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave}>
            {isNew ? 'Add API' : 'Apply'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
